/**
 * Audit Logs viewer — Director-only. Shows every mutation (POST/PUT/DELETE)
 * with user, path, status, duration, and truncated body.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Search, RefreshCw, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_STYLE = {
  POST:   'bg-emerald-100 text-emerald-700',
  PUT:    'bg-blue-100 text-blue-700',
  PATCH:  'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
};

const AuditLogsPage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ method: '', path_contains: '', status_code: '', user_email: '' });
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [list, stat] = await Promise.all([
        axios.get(`${API}/audit-logs?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/audit-logs/stats`, { withCredentials: true }),
      ]);
      setItems(list.data.items || []);
      setStats(stat.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-5" data-testid="audit-logs-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-700 rounded-2xl p-5 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-7 h-7" /> Audit Log
        </h1>
        <p className="text-purple-200 text-sm mt-1">
          Setiap mutasi data direkod — untuk forensic & audit LHDN/SSM.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="audit-stats">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500">Jumlah Rekod</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs text-blue-600">Hari Ini</p>
            <p className="text-2xl font-bold text-blue-700">{stats.today}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <p className="text-xs text-red-600">Gagal (4xx/5xx)</p>
            <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <p className="text-xs text-emerald-600">Top User</p>
            <p className="text-sm font-bold text-emerald-700 truncate">{stats.top_users?.[0]?.email || '—'}</p>
            <p className="text-xs text-emerald-600">{stats.top_users?.[0]?.count || 0} mutasi</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 grid grid-cols-2 md:flex md:flex-row gap-2" data-testid="audit-filters">
        <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-0">
          <option value="">Semua method</option>
          {['POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
        </select>
        <input type="text" placeholder="Path contains..." value={filters.path_contains}
          onChange={(e) => setFilters({ ...filters, path_contains: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-0 col-span-2 md:flex-1" />
        <input type="number" placeholder="Status code" value={filters.status_code}
          onChange={(e) => setFilters({ ...filters, status_code: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-0 md:w-32" />
        <input type="text" placeholder="User email..." value={filters.user_email}
          onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-0" />
        <button onClick={fetchAll} className="col-span-2 md:col-span-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-red-700">
          <Search className="w-4 h-4" /> Cari
        </button>
      </div>

      {/* Table (desktop) */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hidden md:block" data-testid="audit-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="p-3">Masa</th>
                <th className="p-3">Method</th>
                <th className="p-3">Path</th>
                <th className="p-3">Status</th>
                <th className="p-3">User</th>
                <th className="p-3 text-right">Duration</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400">
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin inline" /> : 'Tiada rekod'}
                </td></tr>
              )}
              {items.map((log) => (
                <tr key={log.id} onClick={() => setSelected(log)}
                  className="hover:bg-gray-50 cursor-pointer"
                  data-testid={`audit-row-${log.id}`}>
                  <td className="p-3 text-xs text-gray-600">{log.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${METHOD_STYLE[log.method] || 'bg-gray-100 text-gray-700'}`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-gray-700 font-mono truncate max-w-xs">{log.path}</td>
                  <td className="p-3">
                    <span className={`text-xs font-semibold ${log.status_code >= 400 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {log.status_code}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-gray-600">
                    <p className="truncate">{log.user_email || '—'}</p>
                    <p className="text-gray-400">{log.user_role || ''}</p>
                  </td>
                  <td className="p-3 text-right text-xs text-gray-500">{log.duration_ms}ms</td>
                  <td className="p-3 text-xs text-gray-400 font-mono">{log.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Card layout (mobile < md) */}
      <div className="md:hidden space-y-2" data-testid="audit-cards">
        {items.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">
            {loading ? <RefreshCw className="w-5 h-5 animate-spin inline" /> : 'Tiada rekod'}
          </div>
        )}
        {items.map((log) => (
          <div
            key={log.id}
            onClick={() => setSelected(log)}
            data-testid={`audit-card-${log.id}`}
            className="bg-white rounded-xl border border-gray-100 p-3 active:bg-gray-50 cursor-pointer"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${METHOD_STYLE[log.method] || 'bg-gray-100 text-gray-700'}`}>
                {log.method}
              </span>
              <span className={`text-xs font-semibold shrink-0 ${log.status_code >= 400 ? 'text-red-600' : 'text-emerald-600'}`}>
                {log.status_code}
              </span>
              <span className="text-[11px] text-gray-400 shrink-0">{log.duration_ms}ms</span>
            </div>
            <p className="text-xs text-gray-800 font-mono break-all leading-tight">{log.path}</p>
            <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500 gap-2">
              <span className="truncate">{log.user_email || 'anonymous'} {log.user_role && `• ${log.user_role}`}</span>
              <span className="shrink-0">{log.timestamp?.slice(0, 16).replace('T', ' ')}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {selected && (
        <div onClick={() => setSelected(null)} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6" data-testid="audit-detail">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><Activity className="w-5 h-5" /> Butiran Audit</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <dl className="space-y-2 text-sm">
              {[
                ['Masa', selected.timestamp],
                ['Method', selected.method],
                ['Path', selected.path],
                ['Status', selected.status_code],
                ['User', `${selected.user_email || '—'} (${selected.user_role || '—'})`],
                ['IP', selected.ip],
                ['User Agent', selected.user_agent],
                ['Duration', `${selected.duration_ms} ms`],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 py-1 border-b border-gray-50">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="col-span-2 text-gray-800 font-mono text-xs break-all">{String(v)}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Request Body</h4>
              <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">{selected.request_body || '(empty)'}</pre>
            </div>
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Response Body</h4>
              <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">{selected.response_body || '(empty)'}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
